import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProviders, createProvider, updateProvider, deleteProvider, getCostCenters, getProviderFinancialSummary, getProjects, createFinancialLaunch, createDirectLaunch, uploadDirectReceipt } from '../utils/api';
import { useSharedFile } from '../contexts/SharedFileContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, Receipt, DollarSign, CheckCircle2, Clock, Upload, ArrowLeft, Share2 } from 'lucide-react';
import BulkImport from '../components/BulkImport';

const Providers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sharedFile, clearFile } = useSharedFile();
  const isShareMode = searchParams.get('share') === '1' && !!sharedFile;

  const [providers, setProviders] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cpf_cnpj: '',
    email: '',
    phone: '',
    payment_info: '',
    allowed_services: [],
    is_active: true
  });

  // Financial panel state
  const [financialProvider, setFinancialProvider] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [showNewLaunchForm, setShowNewLaunchForm] = useState(false);
  const [launchType, setLaunchType] = useState('client_material');
  const [projects, setProjects] = useState([]);
  const directFileInputRef = useRef(null);

  // Client launch form
  const [clientLaunchForm, setClientLaunchForm] = useState({
    project_id: '',
    service_code: '',
    service_description: '',
    launch_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_info: ''
  });

  // Direct launch form
  const [directLaunchForm, setDirectLaunchForm] = useState({
    service_code: '',
    service_description: '',
    launch_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    payment_info: '',
    selectedProjects: [],
    receiptFile: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [providersRes, costCentersRes] = await Promise.all([
        getProviders(),
        getCostCenters()
      ]);
      setProviders(providersRes.data);
      setCostCenters(costCentersRes.data);
      
      // Extract all services from all cost centers
      const services = [];
      costCentersRes.data.forEach(cc => {
        cc.services.forEach(service => {
          services.push({
            code: service.code,
            description: service.description,
            cost_center: cc.description
          });
        });
      });
      setAllServices(services);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await updateProvider(editingProvider.id, formData);
        toast.success('Prestador atualizado com sucesso!');
      } else {
        await createProvider(formData);
        toast.success('Prestador criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar prestador');
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      ...provider,
      cpf_cnpj: provider.cpf_cnpj || provider.cnpj || '',  // backward compatibility
      payment_info: provider.payment_info || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este prestador?')) {
      try {
        await deleteProvider(id);
        toast.success('Prestador excluído com sucesso!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao excluir prestador');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      cpf_cnpj: '',
      email: '',
      phone: '',
      payment_info: '',
      allowed_services: [],
      is_active: true
    });
    setEditingProvider(null);
  };

  const toggleService = (serviceCode) => {
    setFormData(prev => ({
      ...prev,
      allowed_services: prev.allowed_services.includes(serviceCode)
        ? prev.allowed_services.filter(code => code !== serviceCode)
        : [...prev.allowed_services, serviceCode]
    }));
  };

  // === Financial Panel Functions ===
  const openFinancialPanel = async (provider) => {
    setFinancialProvider(provider);
    setFinancialLoading(true);
    setShowNewLaunchForm(false);
    try {
      const [summaryRes, projectsRes] = await Promise.all([
        getProviderFinancialSummary(provider.id),
        getProjects()
      ]);
      setFinancialData(summaryRes.data);
      setProjects(projectsRes.data);
      
      // If in share mode, auto-open direct launch form with shared file
      if (isShareMode && sharedFile) {
        setShowNewLaunchForm(true);
        setLaunchType('direct');
        setDirectLaunchForm(prev => ({
          ...prev,
          receiptFile: sharedFile.file,
          payment_info: provider.payment_info || ''
        }));
      }
    } catch (error) {
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setFinancialLoading(false);
    }
  };

  const closeFinancialPanel = () => {
    setFinancialProvider(null);
    setFinancialData(null);
    setShowNewLaunchForm(false);
    resetLaunchForms();
    // Clear share mode
    if (isShareMode) {
      clearFile();
      setSearchParams({});
    }
  };

  const resetLaunchForms = () => {
    setClientLaunchForm({
      project_id: '',
      service_code: '',
      service_description: '',
      launch_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_info: financialProvider?.payment_info || ''
    });
    setDirectLaunchForm({
      service_code: '',
      service_description: '',
      launch_date: new Date().toISOString().split('T')[0],
      total_amount: '',
      payment_info: financialProvider?.payment_info || '',
      selectedProjects: [],
      receiptFile: null
    });
  };

  // Get services available for this provider in a specific project
  const getServicesForProject = (projectId) => {
    if (!financialProvider || !projectId) return [];
    const project = projects.find(p => p.id === projectId);
    const assignment = project?.assignments?.find(a => a.provider_id === financialProvider.id);
    return assignment?.services || [];
  };

  // Get provider's projects (projects where this provider has assignments)
  const getProviderProjects = () => {
    if (!financialProvider) return [];
    return projects.filter(p =>
      p.assignments?.some(a => a.provider_id === financialProvider.id)
    );
  };

  const toggleDirectProject = (projectId) => {
    const isSelected = directLaunchForm.selectedProjects.some(p => p.project_id === projectId);
    if (isSelected) {
      setDirectLaunchForm(prev => ({
        ...prev,
        selectedProjects: prev.selectedProjects.filter(p => p.project_id !== projectId)
      }));
    } else {
      setDirectLaunchForm(prev => ({
        ...prev,
        selectedProjects: [...prev.selectedProjects, { project_id: projectId, amount: '' }]
      }));
    }
  };

  const updateDirectProjectAmount = (projectId, amount) => {
    setDirectLaunchForm(prev => ({
      ...prev,
      selectedProjects: prev.selectedProjects.map(p =>
        p.project_id === projectId ? { ...p, amount } : p
      )
    }));
  };

  const calculateRateioTotal = () => {
    return Math.round(directLaunchForm.selectedProjects.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) * 100) / 100;
  };

  // Get common services across selected direct projects for this provider
  const getDirectCommonServices = () => {
    if (!financialProvider || directLaunchForm.selectedProjects.length === 0) return [];
    const servicesPerProject = directLaunchForm.selectedProjects.map(sp => {
      const project = projects.find(p => p.id === sp.project_id);
      const assignment = project?.assignments?.find(a => a.provider_id === financialProvider.id);
      return assignment?.services || [];
    });
    if (servicesPerProject.length === 0) return [];
    return servicesPerProject[0].filter(service =>
      servicesPerProject.every(ps => ps.some(s => s.service_code === service.service_code))
    );
  };

  const handleSubmitLaunch = async (e) => {
    e.preventDefault();
    if (!financialProvider) return;

    if (launchType === 'client_material') {
      try {
        await createFinancialLaunch({
          project_id: clientLaunchForm.project_id,
          provider_id: financialProvider.id,
          service_code: clientLaunchForm.service_code,
          service_description: clientLaunchForm.service_description,
          launch_date: clientLaunchForm.launch_date,
          amount: parseFloat(clientLaunchForm.amount),
          payment_info: clientLaunchForm.payment_info,
          launch_type: 'client_material'
        });
        toast.success('Lançamento criado com sucesso!');
        setShowNewLaunchForm(false);
        resetLaunchForms();
        openFinancialPanel(financialProvider);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar lançamento');
      }
    } else {
      const totalAmount = parseFloat(directLaunchForm.total_amount);
      const rateioTotal = calculateRateioTotal();
      if (Math.abs(totalAmount - rateioTotal) > 0.01) {
        toast.error(`Soma do rateio (R$ ${rateioTotal.toFixed(2)}) não corresponde ao valor total (R$ ${totalAmount.toFixed(2)})`);
        return;
      }
      if (!directLaunchForm.receiptFile) {
        toast.error('É necessário anexar o comprovante para Lançamento Direto');
        return;
      }
      try {
        const response = await createDirectLaunch({
          provider_id: financialProvider.id,
          service_code: directLaunchForm.service_code,
          service_description: directLaunchForm.service_description,
          launch_date: directLaunchForm.launch_date,
          total_amount: totalAmount,
          payment_info: directLaunchForm.payment_info,
          projects: directLaunchForm.selectedProjects.map(p => ({
            project_id: p.project_id,
            amount: parseFloat(p.amount)
          }))
        });
        if (response.data.length > 0 && response.data[0].direct_launch_group_id) {
          await uploadDirectReceipt(response.data[0].direct_launch_group_id, directLaunchForm.receiptFile);
        }
        toast.success('Lançamento Direto criado com sucesso!');
        setShowNewLaunchForm(false);
        resetLaunchForms();
        openFinancialPanel(financialProvider);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar lançamento direto');
      }
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'paid') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium text-emerald-600 bg-emerald-50">
        <CheckCircle2 className="h-3 w-3" /> Pago
      </span>
    );
    if (status === 'submitted_receipt') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium text-blue-600 bg-blue-50">
        <Upload className="h-3 w-3" /> Comprovante Enviado
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium text-amber-600 bg-amber-50">
        <Clock className="h-3 w-3" /> Pendente
      </span>
    );
  };

  // === Financial Panel Render ===
  if (financialProvider) {
    const providerProjects = getProviderProjects();
    const availableClientServices = getServicesForProject(clientLaunchForm.project_id);
    const directCommonServices = getDirectCommonServices();

    return (
      <div className="space-y-6" data-testid="provider-financial-panel">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={closeFinancialPanel} data-testid="back-to-providers">
            <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900 break-words">
              {financialProvider.name}
            </h1>
            <p className="text-slate-600 font-primary mt-1">Visão financeira do prestador</p>
          </div>
        </div>

        {financialLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-slate-500">Carregando...</p>
          </div>
        ) : financialData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="provider-summary-cards">
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                <p className="font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">Total Previsto</p>
                <p className="text-2xl font-bold text-slate-900 mt-2">R$ {financialData.total_planned.toFixed(2)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                <p className="font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">Total Pago</p>
                <p className="text-2xl font-bold text-purple-600 mt-2">R$ {financialData.total_paid.toFixed(2)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                <p className="font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">Saldo</p>
                <p className={`text-2xl font-bold mt-2 ${financialData.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  R$ {financialData.balance.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900">
                Lançamentos
              </h2>
              <Button
                onClick={() => { setShowNewLaunchForm(!showNewLaunchForm); resetLaunchForms(); }}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto"
                data-testid="new-launch-from-provider"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Lançamento
              </Button>
            </div>

            {/* New Launch Form (inline) */}
            {showNewLaunchForm && (
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6" data-testid="provider-launch-form">
                {/* Type Selector */}
                <div className="mb-4">
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">Tipo de Lançamento</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant={launchType === 'client_material' ? 'default' : 'outline'}
                      onClick={() => { setLaunchType('client_material'); resetLaunchForms(); }}
                      className={`flex-1 ${launchType === 'client_material' ? 'bg-slate-900' : ''}`}>
                      Lançamento (Cliente)
                    </Button>
                    <Button type="button" variant={launchType === 'direct' ? 'default' : 'outline'}
                      onClick={() => { setLaunchType('direct'); resetLaunchForms(); }}
                      className={`flex-1 ${launchType === 'direct' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}>
                      Lançamento Direto
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleSubmitLaunch} className="space-y-4">
                  {launchType === 'client_material' ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Projeto *</Label>
                          <Select value={clientLaunchForm.project_id || undefined}
                            onValueChange={(v) => setClientLaunchForm(prev => ({ ...prev, project_id: v, service_code: '', service_description: '' }))}>
                            <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {providerProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Serviço *</Label>
                          <Select value={clientLaunchForm.service_code || undefined}
                            onValueChange={(v) => {
                              const svc = availableClientServices.find(s => s.service_code === v);
                              setClientLaunchForm(prev => ({ ...prev, service_code: v, service_description: svc?.service_description || '' }));
                            }}
                            disabled={!clientLaunchForm.project_id}>
                            <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {availableClientServices.map((s, i) => <SelectItem key={i} value={s.service_code}>{s.service_code} - {s.service_description}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Data *</Label>
                          <Input type="date" value={clientLaunchForm.launch_date}
                            onChange={(e) => setClientLaunchForm(prev => ({ ...prev, launch_date: e.target.value }))}
                            required className="mt-1 rounded-sm" />
                        </div>
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Valor (R$) *</Label>
                          <Input type="number" step="0.01" value={clientLaunchForm.amount}
                            onChange={(e) => setClientLaunchForm(prev => ({ ...prev, amount: e.target.value }))}
                            required className="mt-1 rounded-sm" />
                        </div>
                      </div>
                      <div>
                        <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Informações de Pagamento</Label>
                        <Textarea value={clientLaunchForm.payment_info}
                          onChange={(e) => setClientLaunchForm(prev => ({ ...prev, payment_info: e.target.value }))}
                          rows={2} className="mt-1 rounded-sm" />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Direct: Project selection */}
                      <div>
                        <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">Projetos (Rateio) *</Label>
                        <div className="border rounded-sm p-3 max-h-40 overflow-y-auto space-y-2">
                          {providerProjects.map(project => {
                            const isChecked = directLaunchForm.selectedProjects.some(p => p.project_id === project.id);
                            return (
                            <div key={project.id}
                              className={`flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-all ${
                                isChecked ? 'bg-purple-50 border border-purple-200' : 'hover:bg-slate-50'
                              }`}
                              onClick={() => toggleDirectProject(project.id)}>
                              <div className={`w-4 h-4 rounded border flex items-center justify-center ${isChecked ? 'bg-purple-600 border-purple-600' : 'border-slate-300'}`}>
                                {isChecked && <span className="text-white text-xs">&#10003;</span>}
                              </div>
                              <span className="text-sm">{project.name}</span>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                      {/* Direct: Service */}
                      {directLaunchForm.selectedProjects.length > 0 && (
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Serviço *</Label>
                          {directCommonServices.length > 0 ? (
                            <select
                              value={directLaunchForm.service_code}
                              onChange={(e) => {
                                const v = e.target.value;
                                const svc = directCommonServices.find(s => s.service_code === v);
                                setDirectLaunchForm(prev => ({ ...prev, service_code: v, service_description: svc?.service_description || '' }));
                              }}
                              className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                              <option value="">Selecione</option>
                              {directCommonServices.map((s, i) => (
                                <option key={i} value={s.service_code}>{s.service_code} - {s.service_description}</option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm text-red-600 mt-1">Sem serviços comuns nos projetos selecionados</p>
                          )}
                        </div>
                      )}
                      {/* Direct: Amount + Date */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Data *</Label>
                          <Input type="date" value={directLaunchForm.launch_date}
                            onChange={(e) => setDirectLaunchForm(prev => ({ ...prev, launch_date: e.target.value }))}
                            required className="mt-1 rounded-sm" />
                        </div>
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Valor Total (R$) *</Label>
                          <Input type="number" step="0.01" value={directLaunchForm.total_amount}
                            onChange={(e) => setDirectLaunchForm(prev => ({ ...prev, total_amount: e.target.value }))}
                            required className="mt-1 rounded-sm" />
                        </div>
                      </div>
                      {/* Direct: Rateio table */}
                      {directLaunchForm.selectedProjects.length > 0 && directLaunchForm.total_amount && (
                        <div>
                          <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">Rateio por Projeto</Label>
                          <div className="border rounded-sm overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="text-left p-2 font-medium">Projeto</th>
                                  <th className="text-right p-2 font-medium w-32">Valor (R$)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {directLaunchForm.selectedProjects.map(sp => {
                                  const proj = projects.find(p => p.id === sp.project_id);
                                  return (
                                    <tr key={sp.project_id} className="border-t">
                                      <td className="p-2">{proj?.name}</td>
                                      <td className="p-2">
                                        <Input type="number" step="0.01" value={sp.amount}
                                          onChange={(e) => updateDirectProjectAmount(sp.project_id, e.target.value)}
                                          className="h-8 text-right" placeholder="0.00" />
                                      </td>
                                    </tr>
                                  );
                                })}
                                <tr className="border-t bg-slate-50 font-medium">
                                  <td className="p-2">Total Rateio</td>
                                  <td className={`p-2 text-right ${
                                    Math.abs(calculateRateioTotal() - parseFloat(directLaunchForm.total_amount || 0)) > 0.01
                                      ? 'text-red-600' : 'text-emerald-600'
                                  }`}>R$ {calculateRateioTotal().toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {/* Direct: Payment info */}
                      <div>
                        <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Informações de Pagamento</Label>
                        <Textarea value={directLaunchForm.payment_info}
                          onChange={(e) => setDirectLaunchForm(prev => ({ ...prev, payment_info: e.target.value }))}
                          rows={2} className="mt-1 rounded-sm" />
                      </div>
                      {/* Direct: Receipt */}
                      <div>
                        <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Comprovante *</Label>
                        <div className="mt-1">
                          <input type="file" ref={directFileInputRef}
                            onChange={(e) => setDirectLaunchForm(prev => ({ ...prev, receiptFile: e.target.files[0] }))}
                            accept=".jpg,.jpeg,.png,.pdf" className="hidden" />
                          <Button type="button" variant="outline" onClick={() => directFileInputRef.current?.click()} className="w-full">
                            <Upload className="h-4 w-4 mr-2" />
                            {directLaunchForm.receiptFile ? directLaunchForm.receiptFile.name : 'Selecionar Arquivo'}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => { setShowNewLaunchForm(false); resetLaunchForms(); }}>Cancelar</Button>
                    <Button type="submit" className={launchType === 'direct' ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-slate-900 text-white hover:bg-slate-800'}>
                      {launchType === 'direct' ? 'Criar Lançamento Direto' : 'Criar Lançamento'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Launch List */}
            <div className="space-y-3">
              {financialData.launches.length > 0 ? (
                financialData.launches.map((launch) => (
                  <div key={launch.id}
                    className={`bg-white border rounded-sm shadow-sm p-4 ${
                      launch.launch_type === 'direct' ? 'border-purple-200' : 'border-slate-200'
                    }`}
                    data-testid="provider-launch-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-900">{launch.service_description}</p>
                          {launch.launch_type === 'direct' && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-sm">Direto</span>
                          )}
                          {launch.direct_launch_group_id && (
                            <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-sm">Rateio</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Projeto: {launch.project_name || '-'} | {launch.service_code}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(launch.launch_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        {getStatusBadge(launch.status)}
                        <p className="font-semibold text-slate-900">R$ {launch.amount?.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 bg-white border border-slate-200 rounded-sm">
                  <Receipt className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Nenhum lançamento encontrado para este prestador</p>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="providers-page">
      {/* Share mode banner */}
      {isShareMode && (
        <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-sm" data-testid="share-mode-banner">
          <Share2 className="h-5 w-5 text-purple-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-purple-900">Arquivo recebido: {sharedFile.fileName}</p>
            <p className="text-xs text-purple-700">Selecione um fornecedor para criar o Lançamento Direto com este comprovante</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { clearFile(); setSearchParams({}); }}
            className="text-purple-600 hover:text-purple-800">Cancelar</Button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
            Prestadores/Fornecedores
          </h1>
          <p className="text-slate-600 font-primary mt-1">Gerencie prestadores e fornecedores</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <BulkImport entity="providers" label="Prestadores" onDone={fetchData} buttonClassName="rounded-sm w-full sm:w-auto" />
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto" data-testid="add-provider-button">
                <Plus className="h-4 w-4 mr-2" />
                <span className="sm:inline">Novo Prestador/Fornecedor</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-secondary uppercase tracking-tight">
                {editingProvider ? 'Editar Prestador/Fornecedor' : 'Novo Prestador/Fornecedor'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="provider-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Nome / Razão Social *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    data-testid="provider-name-input"
                  />
                </div>
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    CPF / CNPJ *
                  </Label>
                  <Input
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    data-testid="provider-cpf-cnpj-input"
                  />
                </div>
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Telefone *
                  </Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    data-testid="provider-phone-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    data-testid="provider-email-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Informações de Pagamento (Chave Pix, Dados Bancários)
                  </Label>
                  <Textarea
                    value={formData.payment_info}
                    onChange={(e) => setFormData({ ...formData, payment_info: e.target.value })}
                    rows={3}
                    className="mt-1 rounded-sm"
                    placeholder="Ex: Chave Pix (CPF): 123.456.789-00&#10;Banco: Itaú | Ag: 1234 | CC: 12345-6"
                    data-testid="provider-payment-info-input"
                  />
                </div>
              </div>

              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">
                  Serviços Permitidos *
                </Label>
                <div className="border border-slate-200 rounded-sm p-4 max-h-64 overflow-y-auto">
                  {costCenters.map(cc => (
                    <div key={cc.id} className="mb-4">
                      <p className="font-semibold text-sm text-slate-700 mb-2">{cc.description}</p>
                      <div className="space-y-2 ml-4">
                        {cc.services.map(service => (
                          <div key={service.code} className="flex items-center space-x-2">
                            <Checkbox
                              id={`service-${service.code}`}
                              checked={formData.allowed_services.includes(service.code)}
                              onCheckedChange={() => toggleService(service.code)}
                              data-testid={`service-checkbox-${service.code}`}
                            />
                            <label htmlFor={`service-${service.code}`} className="text-sm text-slate-600 cursor-pointer">
                              {service.code} - {service.description}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {formData.allowed_services.length === 0 && (
                  <p className="text-xs text-red-600 mt-1">Selecione pelo menos um serviço</p>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" 
                  data-testid="submit-provider-button"
                  disabled={formData.allowed_services.length === 0}
                >
                  {editingProvider ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 hover:border-slate-300 hover:shadow-md transition-all"
            data-testid="provider-card"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-sm bg-accent/10">
                <Building2 className="h-5 w-5 text-accent" strokeWidth={1.5} />
              </div>
              <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                provider.is_active ? 'text-emerald-600 bg-emerald-50' : 'text-slate-600 bg-slate-100'
              }`}>
                {provider.is_active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <h3 className="font-semibold text-slate-900 text-lg mb-1 break-words">{provider.name}</h3>
            <p className="text-xs text-slate-500 mb-2 break-words">CPF/CNPJ: {provider.cpf_cnpj || provider.cnpj}</p>
            <p className="text-sm text-slate-600 mb-1 break-all">{provider.email}</p>
            <p className="text-sm text-slate-600 mb-3 break-words">{provider.phone}</p>
            {provider.payment_info && (
              <p className="text-xs text-slate-500 mb-2 line-clamp-2 break-words">
                Pagamento: {provider.payment_info}
              </p>
            )}
            <p className="text-xs text-slate-500 mb-3">
              {provider.allowed_services?.length || 0} serviço(s) permitido(s)
            </p>
            <div className="flex gap-2 mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openFinancialPanel(provider)}
                className="flex-1 rounded-sm"
                data-testid="provider-financial-button"
              >
                <DollarSign className="h-3 w-3 mr-1" />
                Lançamentos
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEdit(provider)}
                className="rounded-sm"
                data-testid="edit-provider-button"
              >
                <Edit className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(provider.id)}
                className="rounded-sm"
                data-testid="delete-provider-button"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {providers.length === 0 && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-slate-600">Nenhum prestador/fornecedor cadastrado</p>
        </div>
      )}
    </div>
  );
};

export default Providers;
